import {
  format,
  formatDistanceToNowStrict,
  isToday,
  isTomorrow,
  Locale,
} from "date-fns";
import { InstantRange } from "../../api/scheduleApi.ts";
import { BookingTimeslotResponseDto } from "../../api/bookingApi.ts";

/**
 * Formats just the date portion of a timeslot, e.g. "Wed, Jun 18"
 */
export function formatDate(date: Date, locale: Locale): string {
  return format(new Date(date), "EEE, MMM d", { locale });
}

/**
 * Formats a timeslot as "Wed, Jun 18 · 14:00 – 15:00"
 */
export function formatTimeSlotLabel(
  timeSlot: InstantRange,
  locale: Locale,
): string {
  const date = formatDate(timeSlot.from, locale);
  const fromTime = format(new Date(timeSlot.from), "HH:mm", { locale });
  const toTime = format(new Date(timeSlot.to), "HH:mm", { locale });

  return `${date} · ${fromTime} – ${toTime}`;
}

/**
 * Formats just the time range portion of a timeslot, e.g. "14:00 – 15:00"
 */
export function formatTimeRange(
  timeSlot: InstantRange,
  locale: Locale,
): string {
  const fromTime = format(new Date(timeSlot.from), "HH:mm", { locale });
  const toTime = format(new Date(timeSlot.to), "HH:mm", { locale });

  return `${fromTime} – ${toTime}`;
}

/**
 * Returns a clean relative time string like "in 3 hours" or "in 5 days".
 * Uses strict mode to avoid vague prefixes like "about" or "less than".
 */
export function formatRelativeTime(date: Date, locale: Locale): string {
  return formatDistanceToNowStrict(new Date(date), {
    addSuffix: true,
    locale,
  });
}

/**
 * Returns a contextual short label for a date.
 * - "Today" / "Tomorrow" when applicable
 * - Otherwise the formatted date like "Wed, Jun 18"
 */
export function formatSmartDate(
  date: Date,
  locale: Locale,
  todayLabel: string,
  tomorrowLabel: string,
): string {
  const d = new Date(date);
  if (isToday(d)) return todayLabel;
  if (isTomorrow(d)) return tomorrowLabel;
  return formatDate(d, locale);
}

/**
 * Filters timeslots to only future, non-cancelled ones.
 */
export function getFutureTimeSlots(
  timeSlots: BookingTimeslotResponseDto[],
): BookingTimeslotResponseDto[] {
  const now = new Date();
  return timeSlots.filter(
    (slot) => new Date(slot.to) > now && slot.status !== "CANCELLED",
  );
}
